import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { claimInstallMarker } from '@/tauri/install-tracking';
import { isTauri } from '@/tauri/utils';
import {
  getDesktopBuildId,
  getDesktopVersion,
  initDesktopVersion,
} from '@/utils/appVersion';
import { logger } from '@/utils/logger/logger';
import { useEffect, useRef } from 'react';

/**
 * Fires `application_install` once on a desktop fresh install. Resolve version
 * before claiming: `version` is required by the schema, so if it's unresolved
 * (only in local `tauri:dev`) we bail without claiming and retry next launch,
 * rather than burning the marker on an event that would fail validation.
 */
export function useTrackApplicationInstall(): void {
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!isTauri()) return;
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async () => {
      try {
        await initDesktopVersion();
        const version = getDesktopVersion();
        if (!version) return;

        const isFirstLaunch = await claimInstallMarker();
        if (!isFirstLaunch) return;

        const build = getDesktopBuildId();
        track({
          event_name: TrackingEventName.ApplicationInstalled,
          props: { version, platform: 'desktop', ...(build ? { build } : {}) },
          options: { amplitude: false },
        });
      } catch (error) {
        logger.error('[applicationInstall] tracking failed', (error as Error)?.message);
      }
    };

    void run();
  }, []);
}

export function ApplicationInstallSync() {
  useTrackApplicationInstall();
  return null;
}
